import { z } from 'zod';
import {
  ATTAINMENT_TONES,
  type AttainmentTone,
  BLOCKER_ACTION_KINDS,
  DAILY_DTO_STATUSES,
  DAILY_EXEMPT_REASONS,
  KPI_UNITS,
  LATE_SUBMISSION_UNTIL,
  REPORT_TASK_ACTIONS,
  REPORT_VALUE_SOURCES,
  TARGET_PERIOD_TYPES,
} from '../enums/ptstdm';
import { isoDateTimeSchema, localDateSchema, singleLineText, uuidSchema } from './common';
import {
  agendaTaskDtoSchema,
  checklistItemDtoSchema,
  decimalInputSchema,
  decimalStringSchema,
  feedAvailabilitySchema,
  hhmmSchema,
  reasonSchema,
} from './ptstdm-common';
import { checklistEvidenceInputSchema } from './ptstdm-checklists';

/**
 * "D" of PTSTDM (PLAN §4 "Morning agenda, evening report, manager daily view", SPEC §7.5): the
 * pre-filled morning agenda with run-rate targets and their calculation text, the evening report
 * pre-filled from the agenda, the manager daily view and the department windows. Each form loads
 * from one GET (the 60-second budget). Per-person rows follow `report-view(U)` (D-093); `GET
 * /daily/team` gives other callers aggregates only. The daily workstream owns the routes
 * (`apps/api/src/modules/daily/`); `:date` is `YYYY-MM-DD` or `today`.
 */

const windowSchema = z.object({ opensAt: isoDateTimeSchema, closesAt: isoDateTimeSchema });

// ---------------------------------------------------------------- AgendaDto (PLAN §4)

export const agendaKpiTargetDtoSchema = z.object({
  kpiId: z.string(),
  name: z.string(),
  unit: z.enum(KPI_UNITS),
  decimalPlaces: z.number().int(),
  direction: z.enum(['higher_better', 'lower_better']),
  periodType: z.enum(TARGET_PERIOD_TYPES),
  periodStart: localDateSchema,
  periodTarget: decimalStringSchema.nullable(),
  periodToDateActual: decimalStringSchema,
  /** Including today; a half-day leave counts 0.5. */
  remainingWorkingDays: decimalStringSchema,
  baseDailyEffective: decimalStringSchema.nullable(),
  computedTarget: decimalStringSchema.nullable(),
  committedTarget: decimalStringSchema.nullable(),
  changeKind: z.enum(['none', 'raised', 'lowered']),
  /** Making the target easier needs a reason, visible to the manager. */
  changeReason: z.string().nullable(),
  /** "Month target 1,000 calls · done 420 · 11 working days left → today 53". */
  calculation: z.string(),
});
export type AgendaKpiTargetDto = z.infer<typeof agendaKpiTargetDtoSchema>;

export const topPrioritySchema = z.object({
  text: z.string(),
  taskId: uuidSchema.nullable(),
});

export const agendaDtoSchema = z.object({
  date: localDateSchema,
  userId: uuidSchema,
  /** `not_started` = no row yet (before the window, or the builder has not run). */
  status: z.enum(DAILY_DTO_STATUSES),
  exemptReason: z.enum(DAILY_EXEMPT_REASONS).nullable(),
  /** True after the window closes; later edits need `lateEditReason`. */
  locked: z.boolean(),
  window: windowSchema,
  kpiTargets: z.array(agendaKpiTargetDtoSchema),
  /** Due today, with due times. */
  checklist: z.array(checklistItemDtoSchema),
  tasks: z.object({
    dueToday: z.array(agendaTaskDtoSchema),
    overdue: z.array(agendaTaskDtoSchema),
    awaitingMyAcceptance: z.array(agendaTaskDtoSchema),
  }),
  training: z.array(
    z.object({ assignmentId: uuidSchema, moduleTitle: z.string(), dueDate: localDateSchema }),
  ),
  meetings: z.array(z.object({ id: uuidSchema, title: z.string(), startsAt: isoDateTimeSchema })),
  /** Tasks (Phase 4), training and meetings (Phase 7) arrive through ports (D-252). */
  feeds: z.object({
    tasks: feedAvailabilitySchema,
    training: feedAvailabilitySchema,
    meetings: feedAvailabilitySchema,
  }),
  topPriorities: z.array(topPrioritySchema).max(3),
  /** True when the priorities are suggestions (no saved priorities yet). */
  prioritiesSuggested: z.boolean(),
  firstOpenedAt: isoDateTimeSchema.nullable(),
  submittedAt: isoDateTimeSchema.nullable(),
  lateEdits: z.array(
    z.object({ at: isoDateTimeSchema, reason: z.string(), fieldPath: z.string() }),
  ),
  /** Whether the caller may edit (self, within the rules above). */
  editable: z.boolean(),
  version: z.number().int().nonnegative(),
});
export type AgendaDto = z.infer<typeof agendaDtoSchema>;

export const dailyUserQuerySchema = z.object({ userId: uuidSchema.optional() });

export const saveAgendaRequestSchema = z.strictObject({
  kpiTargets: z
    .array(
      z.strictObject({
        kpiId: uuidSchema,
        committedTarget: decimalInputSchema,
        changeReason: reasonSchema.optional(),
      }),
    )
    .max(20)
    .optional(),
  topPriorities: z
    .array(z.strictObject({ text: singleLineText(1, 200), taskId: uuidSchema.optional() }))
    .max(3)
    .optional(),
});
export type SaveAgendaRequest = z.infer<typeof saveAgendaRequestSchema>;

export const commitAgendaRequestSchema = saveAgendaRequestSchema.extend({
  /** After the window: the edit is logged and visible to the manager. */
  lateEditReason: reasonSchema.optional(),
});
export type CommitAgendaRequest = z.infer<typeof commitAgendaRequestSchema>;

// ---------------------------------------------------------------- ReportDto (PLAN §4)

export const reportKpiActualDtoSchema = z.object({
  kpiId: uuidSchema,
  name: z.string(),
  unit: z.enum(KPI_UNITS),
  decimalPlaces: z.number().int(),
  planned: decimalStringSchema.nullable(),
  actual: decimalStringSchema.nullable(),
  source: z.enum(REPORT_VALUE_SOURCES),
  /** True when a system or derived value exists: shown read-only with its source. */
  readOnly: z.boolean(),
  /** "from telephony". */
  sourceLabel: z.string().nullable(),
  entryId: uuidSchema.nullable(),
  verificationStatus: z.enum(['unverified', 'verified', 'rejected']).nullable(),
  discrepancyFlagged: z.boolean(),
  attainmentPct: decimalStringSchema.nullable(),
});

export const reportDtoSchema = z.object({
  date: localDateSchema,
  userId: uuidSchema,
  status: z.enum(DAILY_DTO_STATUSES),
  exemptReason: z.enum(DAILY_EXEMPT_REASONS).nullable(),
  window: windowSchema.extend({ lateCutoffAt: isoDateTimeSchema }),
  locked: z.boolean(),
  kpiActuals: z.array(reportKpiActualDtoSchema),
  checklist: z.array(checklistItemDtoSchema),
  tasks: z.object({
    completedToday: z.array(agendaTaskDtoSchema),
    open: z.array(agendaTaskDtoSchema),
  }),
  feeds: z.object({ tasks: feedAvailabilitySchema }),
  blockers: z.array(
    z.object({
      id: uuidSchema.nullable(),
      text: z.string(),
      needsAction: z.boolean(),
      actionKind: z.enum(BLOCKER_ACTION_KINDS).nullable(),
      helpFromUserId: uuidSchema.nullable(),
      createdTaskId: uuidSchema.nullable(),
    }),
  ),
  /** Live achievement % (KPI lines capped at 100, blended with checklist completion, sec. 13.8). */
  achievementPct: decimalStringSchema.nullable(),
  kpiPartPct: decimalStringSchema.nullable(),
  checklistPartPct: decimalStringSchema.nullable(),
  thresholdPct: decimalStringSchema,
  belowThreshold: z.boolean(),
  lowAchievementReason: z.string().nullable(),
  notes: z.string().nullable(),
  firstOpenedAt: isoDateTimeSchema.nullable(),
  submittedAt: isoDateTimeSchema.nullable(),
  editable: z.boolean(),
  version: z.number().int().nonnegative(),
});
export type ReportDto = z.infer<typeof reportDtoSchema>;

const reportBlockerInputSchema = z.strictObject({
  text: z.string().trim().min(1).max(2000),
  needsAction: z.boolean().default(false),
  /** Creates a task or a decision request through the task-feed port (Phase 4). */
  createAs: z.enum(BLOCKER_ACTION_KINDS).optional(),
  assigneeUserId: uuidSchema.optional(),
  dueDate: localDateSchema.optional(),
});

export const submitReportRequestSchema = z.strictObject({
  /** Self-reported KPIs only (system values are read-only). */
  kpiActuals: z
    .array(
      z.strictObject({
        kpiId: uuidSchema,
        value: decimalInputSchema,
        evidenceFileIds: z.array(uuidSchema).max(5).optional(),
        note: z.string().trim().max(2000).optional(),
      }),
    )
    .max(20)
    .optional(),
  checklist: z
    .array(
      z.strictObject({
        itemId: uuidSchema,
        status: z.enum(['done', 'partial', 'not_done']),
        reason: reasonSchema.optional(),
        evidence: checklistEvidenceInputSchema.optional(),
      }),
    )
    .max(200)
    .optional(),
  taskUpdates: z
    .array(
      z.strictObject({
        taskId: uuidSchema,
        action: z.enum(REPORT_TASK_ACTIONS),
        note: z.string().trim().max(2000).optional(),
      }),
    )
    .max(100)
    .optional(),
  blockers: z.array(reportBlockerInputSchema).max(20).optional(),
  notes: z.string().trim().max(4000).optional(),
  lowAchievementReason: reasonSchema.optional(),
  lateReason: reasonSchema.optional(),
});
export type SubmitReportRequest = z.infer<typeof submitReportRequestSchema>;

export const saveReportRequestSchema = submitReportRequestSchema.partial();
export const editReportRequestSchema = submitReportRequestSchema.partial().extend({
  reason: reasonSchema,
});
export const submitReportResponseSchema = z.object({
  report: reportDtoSchema,
  createdTaskIds: z.array(uuidSchema),
});

// ---------------------------------------------------------------- manager daily view

export const teamDailyQuerySchema = z.object({
  date: z.union([localDateSchema, z.literal('today')]).default('today'),
  departmentId: uuidSchema.optional(),
  managerUserId: uuidSchema.optional(),
});

export const teamDailyMemberSchema = z.object({
  userId: uuidSchema,
  agendaStatus: z.enum(DAILY_DTO_STATUSES),
  reportStatus: z.enum(DAILY_DTO_STATUSES),
  exemptReason: z.enum(DAILY_EXEMPT_REASONS).nullable(),
  agendaFirstOpenedAt: isoDateTimeSchema.nullable(),
  agendaSubmittedAt: isoDateTimeSchema.nullable(),
  reportFirstOpenedAt: isoDateTimeSchema.nullable(),
  reportSubmittedAt: isoDateTimeSchema.nullable(),
  /** Submitted − first opened (the 60-second measurement). */
  agendaSeconds: z.number().int().nullable(),
  reportSeconds: z.number().int().nullable(),
  kpis: z.array(
    z.object({
      kpiId: uuidSchema,
      plan: decimalStringSchema.nullable(),
      actual: decimalStringSchema.nullable(),
      pct: decimalStringSchema.nullable(),
      verificationStatus: z.enum(['unverified', 'verified', 'rejected']).nullable(),
    }),
  ),
  loweredTargets: z.array(
    z.object({
      kpiId: uuidSchema,
      computedTarget: decimalStringSchema,
      committedTarget: decimalStringSchema,
      changeReason: z.string(),
    }),
  ),
  achievementPct: decimalStringSchema.nullable(),
  checklistCompletionPct: decimalStringSchema.nullable(),
  missedCritical: z.array(z.object({ itemId: uuidSchema, name: z.string() })),
  blockers: z.array(
    z.object({ text: z.string(), needsAction: z.boolean(), createdTaskId: uuidSchema.nullable() }),
  ),
});

export const teamDailyResponseSchema = z.object({
  date: localDateSchema,
  /** Only people the caller has `report-view` on; everyone else counts in `summary` only. */
  members: z.array(teamDailyMemberSchema),
  checklistVerificationPending: z.number().int().nonnegative(),
  kpiVerificationPending: z.number().int().nonnegative(),
  summary: z.object({
    people: z.number().int().nonnegative(),
    expected: z.number().int().nonnegative(),
    agendasSubmitted: z.number().int().nonnegative(),
    reportsSubmitted: z.number().int().nonnegative(),
    reportCompliancePct: decimalStringSchema.nullable(),
    avgAchievementPct: decimalStringSchema.nullable(),
    missedCriticalCount: z.number().int().nonnegative(),
  }),
});
export type TeamDailyResponse = z.infer<typeof teamDailyResponseSchema>;

// ---------------------------------------------------------------- department windows (sec. 9.1)

const timeWindowSchema = z.object({ start: hhmmSchema, end: hhmmSchema });

export const departmentPtstdmSettingsDtoSchema = z.object({
  departmentId: uuidSchema,
  headUserId: uuidSchema.nullable(),
  enabled: z.boolean(),
  dailyChannelId: uuidSchema.nullable(),
  agendaWindow: timeWindowSchema,
  reportWindow: timeWindowSchema,
  achievementThresholdPct: decimalStringSchema,
  checklistSharePct: decimalStringSchema,
  reportReminderMinutesBeforeClose: z.number().int(),
  agendaReminderMinutesBeforeClose: z.number().int(),
  lateSubmissionUntil: z.enum(LATE_SUBMISSION_UNTIL),
  /** ISO weekdays; null = the workspace's `calendar.workingDays`. */
  workingWeekdays: z.array(z.number().int().min(1).max(7)).nullable(),
  version: z.number().int().nonnegative(),
});
export type DepartmentPtstdmSettingsDto = z.infer<typeof departmentPtstdmSettingsDtoSchema>;

export const updateDepartmentPtstdmSettingsRequestSchema = z.strictObject({
  /** admin+ only (audited `department.head_changed`). */
  headUserId: uuidSchema.nullable().optional(),
  enabled: z.boolean().optional(),
  /** A public channel. */
  dailyChannelId: uuidSchema.nullable().optional(),
  agendaWindow: z.strictObject({ start: hhmmSchema, end: hhmmSchema }).optional(),
  reportWindow: z.strictObject({ start: hhmmSchema, end: hhmmSchema }).optional(),
  achievementThresholdPct: z.number().min(0).max(100).optional(),
  checklistSharePct: z.number().min(0).max(100).optional(),
  reportReminderMinutesBeforeClose: z.int().min(5).max(120).optional(),
  agendaReminderMinutesBeforeClose: z.int().min(5).max(55).optional(),
  lateSubmissionUntil: z.enum(LATE_SUBMISSION_UNTIL).optional(),
  workingWeekdays: z.array(z.int().min(1).max(7)).min(1).max(7).nullable().optional(),
});
export const departmentSettingsParamsSchema = z.object({ departmentId: uuidSchema });

// ---------------------------------------------------------------- daily cards (messages.metadata, D-255)

/**
 * The colour band of an attainment % (`ATTAINMENT_TONES`): the API stores it on the card and the
 * web colours by it, so both agree. `thresholdPct` is the department's `achievementThresholdPct`.
 */
export function attainmentTone(
  pct: string | number | null | undefined,
  thresholdPct: number,
): AttainmentTone {
  if (pct === null || pct === undefined || pct === '') return 'none';
  const value = typeof pct === 'number' ? pct : Number(pct);
  if (!Number.isFinite(value)) return 'none';
  if (value >= 100) return 'met';
  return value >= thresholdPct ? 'near' : 'below';
}

const cardKpiLineSchema = z.object({
  kpiId: uuidSchema,
  name: z.string(),
  unit: z.enum(KPI_UNITS),
  decimalPlaces: z.number().int(),
  /** The committed daily target (the plan). */
  plan: decimalStringSchema.nullable(),
  /** Null until the evening report. */
  actual: decimalStringSchema.nullable(),
  attainmentPct: decimalStringSchema.nullable(),
  tone: z.enum(ATTAINMENT_TONES),
});

/**
 * `messages.metadata` of the compact card in the department daily channel (`kind = 'card'`,
 * subtype `agenda_card`; SPEC §7.5): posted when the agenda is submitted (`stage = 'plan'`) and
 * updated in place by the evening report to plan vs actual (`stage = 'plan_vs_actual'`) with
 * `messageSendService.updateCardMessage`. Every member of the channel sees it, so it carries summary
 * fields only (PLAN architecture §5.1): never blockers, reasons, lowered-target reasons, discrepancy
 * flags or notes. The message's plain text repeats the same summary for notifications and search.
 */
export const dailyChannelCardSchema = z.object({
  v: z.literal(1),
  card: z.literal('daily_channel'),
  stage: z.enum(['plan', 'plan_vs_actual']),
  userId: uuidSchema,
  departmentId: uuidSchema,
  date: localDateSchema,
  agendaId: uuidSchema,
  reportId: uuidSchema.nullable(),
  /** Submitted after its window (the agenda or, once updated, the report). */
  late: z.boolean(),
  thresholdPct: z.number().min(0).max(100),
  kpis: z.array(cardKpiLineSchema),
  checklist: z.object({
    due: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
  }),
  /** The day's achievement % (PLAN §3.3 sec. 13.8); null at the plan stage. */
  achievementPct: decimalStringSchema.nullable(),
  achievementTone: z.enum(ATTAINMENT_TONES),
});
export type DailyChannelCard = z.infer<typeof dailyChannelCardSchema>;

/**
 * `messages.metadata` of the bot DM that opens the morning agenda (subtype `agenda_card`) or the
 * evening report (subtype `report_card`) with one tap (PLAN architecture §3.5: `clientMsgId`
 * derived from `agenda:{userId}:{date}` / `report:{userId}:{date}`).
 */
export const dailyPromptCardSchema = z.object({
  v: z.literal(1),
  card: z.literal('daily_prompt'),
  form: z.enum(['agenda', 'report']),
  userId: uuidSchema,
  date: localDateSchema,
  closesAt: isoDateTimeSchema,
  /** Pre-filled counts shown on the card, e.g. 3 KPI targets, 5 checklist items due. */
  kpiCount: z.number().int().nonnegative(),
  checklistDue: z.number().int().nonnegative(),
});
export type DailyPromptCard = z.infer<typeof dailyPromptCardSchema>;

/** Either daily card, told apart by `card`. */
export const dailyCardSchema = z.discriminatedUnion('card', [
  dailyChannelCardSchema,
  dailyPromptCardSchema,
]);
export type DailyCard = z.infer<typeof dailyCardSchema>;
